import * as _ from "lodash";
import { PlatformActions, NativeActions, UserInfo, ImageInfo, FileSelection, ScreenshotCropConfig, TrackingIdInfo, Accounts, LocalServerActions } from "../PlatformActions";
import { PlatformEvent, PlatformEventHandler, RecordingStatus } from "../NativeCommChannel";
import { VSInstanceInfo } from "../../store/appState";
import { SettingsUpdate } from "../../SettingsUpdate";
import { Settings } from "../../Settings";
import { stringFromErrorOrAny, delay } from "../../utils/tools";
import additionalFiles from "./additionalFiles";
import vsInstances, { newVSInstances } from "./vsInstances";
import { FileUploadConfig } from "../../DataCollectorConfiguration";
import { platformEventHandler } from "../Communication";
import { Apis } from "../../store/actions";

import fakeProfileDefault from "./fakeProfileDefault";
import fakeProfileError from "./fakeProfileError";
import activities from "./activities";
import profileAttachments from "./profileAttachments";
import downloadUrl from "./downloadUrl";
import exportStatus from "./exportStatus";

export interface FakePlatformActionsConfig {
    authentication: {
        errorMessage?: string;
        signinDelay: number;
    };
    disconnectionDelay?: number;
    ajaxRequestDelay?: number;
    clientError?: {
        show: boolean;
        delay: number;
        errorMessage: string;
        title?: string;
        canDismiss?: boolean;
    };
    vsInstancesFetch?: {
        errorMessage?: string;
    };
    vsInstanceNew?: {
        errorMessage?: string;
    };
    getScreenshot?: {
        errorMessage?: string;
    };
    recording: {
        result: "success" | "error" | "cancel";
        files?: string[];
        errorMessage?: string;
        delayToProcessing: number;
        delayToDone: number;
    };
    updatedSysLogs?: {
        filesToUpload: string[];
        filesToUploadPerAssembly?: {
            assemblyName: string;
            filePaths: string[];
        }[];
        delayForUpdate: number;
    };
    updateSettings: {
        errorMessage?: string;
    };
    selectFiles?: {
        errorMessage?: string;
    };
    viewFile?: {
        errorMessage?: string;
    };
    upload?: {
        errorMessage?: string;
    };
    reserveNewTrackingId?: {
        errorMessage?: string;
    };
}

// Provides an easy way to test the frontend manually and using automation without spinning up VS and native process
export class FakePlatformActions implements PlatformActions, NativeActions, LocalServerActions {
    private platformEventHandler: PlatformEventHandler;
    private settings: () => Settings;
    private vsInstances: VSInstanceInfo[] = vsInstances;
    private newVSInstances: VSInstanceInfo[] = newVSInstances;
    private additionalFiles: FileSelection[] = additionalFiles;
    private backupActions: PlatformActions;
    private config: FakePlatformActionsConfig;

    constructor(settings: () => Settings, backupActions: PlatformActions, config: FakePlatformActionsConfig | string | null = null) {
        this.settings = settings;
        this.backupActions = backupActions;
        this.platformEventHandler = platformEventHandler;
        this.config = fakeProfileDefault;

        if (config) {
            if (typeof config === "string") {
                switch (config) {
                    case "error":
                        this.config = fakeProfileError;
                        break
                }
            } else {
                this.config = config;
            }
        }

        console.log("********** Using FakePlatformActions **********");
        setTimeout(() => {
            this.hello("dummy-id").then((settings: Settings) => {
                console.log("Hello response");
                console.log(PlatformEvent.serverSettings);
                console.log(settings);
            }).catch((reason: any) => {
                console.error(stringFromErrorOrAny(reason));
                console.log(PlatformEvent.clientError + ': ' + stringFromErrorOrAny(reason));
            })
        }, 10);
    }

    private async hello(fsid: string): Promise<Settings> {
        if (this.config.disconnectionDelay) {
            setTimeout(() => {
                this.platformEventHandler(PlatformEvent.disconnected);
            }, this.config.disconnectionDelay);
        }

        if (this.config.clientError?.show) {
            setTimeout(() => {
                this.platformEventHandler(PlatformEvent.clientError, this.config.clientError);
            }, this.config.clientError.delay);
        }

        if (this.config.updatedSysLogs?.filesToUpload.length !== 0) {
            setTimeout(() => {
                this.platformEventHandler(PlatformEvent.fileCheck, this.config.updatedSysLogs);
            }, this.config.updatedSysLogs.delayForUpdate);
        }

        return _.cloneDeep(this.settings());
    }

    public async get(authRequired: boolean, partialUri: string): Promise<any> {
        return delay(this.config.ajaxRequestDelay ?? 100)
            .then(() => {
                const authRequiredToUse = true;    // Ignore auth for fake actions
                const routedAPIs = [
                    Apis.SearchQuerySearchV2,
                    Apis.DetailsV3RootPost,
                ].map(x => x.toString());

                // if we have fallback server and if the partialUri is found in routedAPIs
                if (this.backupActions && routedAPIs.some(r => partialUri.indexOf(r) !== -1)) {
                    return this.backupActions.get(authRequiredToUse, partialUri);
                }
                const partialUriSplit = partialUri.split("?");
                const path = partialUriSplit[0];
                if (path === Apis.DiagnosticsAttachmentsPerUser) {
                    return this.handleDiagnosticsAttachmentsPerUser(path)
                }
                if (path === Apis.ExportRestrictStatus) {
                    return this.handleExportRestrictStatus(path)
                }
                throw Error("NotImplemented: get: " + partialUri);
            });
    }

    private handleDiagnosticsAttachmentsPerUser(partialUri: Apis): any {
        this.startGroup(partialUri);
        this.endGroup(partialUri);
        return _.cloneDeep(profileAttachments);
    }

    private handleExportRestrictStatus(partialUri: Apis): any {
        this.startGroup(partialUri);
        this.endGroup(partialUri);
        return _.cloneDeep(exportStatus);
    }

    // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
    public async post(authRequired: boolean, partialUri: string, data: any): Promise<any> {
        return delay(this.config.ajaxRequestDelay ?? 100)
            .then(() => {
                const authRequiredToUse = false;    // Ignore auth for fake actions
                const routedAPIs = [
                    Apis.SearchQuerySearchV2,
                    Apis.DetailsV3RootPost,
                    Apis.DiagnosticsApproveFilesToUpload,
                    Apis.SearchQueryItem,
                ].map(x => x.toString());

                if (this.backupActions && routedAPIs.indexOf(partialUri) !== -1) {
                    this.startGroup(partialUri);
                    console.log(`authRequired: ${authRequired}`);
                    console.log(data);
                    this.endGroup(partialUri);
                    return this.backupActions.post(authRequiredToUse, partialUri, data);
                }
                if (partialUri === Apis.V2Verbatim) {
                    return this.handlePostV2Verbatim(partialUri, authRequiredToUse, data);
                }
                if (partialUri === Apis.DetailsV2SubmitComment) {
                    return this.handlePostDetailsV2SubmitComment(partialUri, authRequiredToUse, data);
                }
                if (partialUri === Apis.UserPropertiesGetActivity) {
                    return this.handlePostUserPropertiesGetActivity(partialUri, authRequiredToUse, data);
                }
                // TODO: add fake exportRestrict/export call
                // TODO: add fake exportRestrict/delete call
                throw Error("NotImplemented: post: " + partialUri + ", " + JSON.stringify(data));
            });
    }

    private handlePostV2Verbatim(partialUri: Apis, authRequired: boolean, data: any): any {
        this.startGroup(partialUri);
        console.log(`authRequired: ${authRequired}`);
        console.log(data);
        this.endGroup(partialUri);
        return {
            data: {
                trackingId: "fake-tracking-id"
            }
        };
    }

    private handlePostDetailsV2SubmitComment(partialUri: Apis, authRequired: boolean, data: any): any {
        this.startGroup(partialUri);
        console.log(`authRequired: ${authRequired}`);
        console.log(data);
        this.endGroup(partialUri);
        return {
            data: {
                trackingId: "fake-tracking-id",
                commentTrackingId: "fake-comment-tracking-id"
            }
        };
    }

    private handlePostDetailsV2GetNeedMoreInfoComments(partialUri: Apis, authRequired: boolean, data: any): any {
        this.startGroup(partialUri);
        console.log(`authRequired: ${authRequired}`);
        console.log(data);
        this.endGroup(partialUri);
        return {
            data: {
                trackingId: "fake-tracking-id",
                commentTrackingId: "fake-comment-tracking-id"
            }
        };
    }

    private handlePostUserPropertiesGetActivity(partialUri: Apis, authRequired: boolean, data: any): any {
        this.startGroup(partialUri);
        console.log(`authRequired: ${authRequired}`);
        console.log(data);
        this.endGroup(partialUri);
        return _.cloneDeep(activities);
    }

    private startGroup(text: string): void {
        console.log(`***************** <${text}> *****************`);
    }

    private endGroup(text: string): void {
        console.log(`***************** </${text}> *****************`);
    }

    public async signOutAndIn(): Promise<UserInfo> {
        throw Error("NotImplemented: signOutAndIn");
    }

    public async signOut(): Promise<void> {
        throw Error("NotImplemented: signOut");
    }

    public async checkForSignIn(): Promise<Accounts | UserInfo | null> {
        return new Promise(resolve => {
            import("./userInfo").then((userInfo) => {
                setTimeout(() => { resolve(userInfo.default) }, this.config.authentication.signinDelay)
            });
        });
    }

    public getNativeActions = (): NativeActions => this;
    public getLocalServerActions = (): LocalServerActions => this;

    public async newVsInstance(): Promise<void> {
        if (this.config.vsInstanceNew?.errorMessage) {
            throw new Error(this.config.vsInstanceNew.errorMessage);
        }
        if (this.newVSInstances.length === 0) {
            throw Error("Can't create any more VS Instances");
        }
        this.vsInstances.push(this.newVSInstances.shift());
    }

    // It is important to return cloned object.
    // Otherwise redux stores this same reference in store leading to issues
    public getRunningVsInstances = async (): Promise<VSInstanceInfo[]> => {
        if (this.config.vsInstancesFetch?.errorMessage) {
            throw new Error(this.config.vsInstancesFetch.errorMessage);
        }
        return _.cloneDeep(this.vsInstances);
    }

    updateSettings(vsPid: number): Promise<SettingsUpdate> {
        if (this.config.updateSettings?.errorMessage) {
            throw new Error(this.config.updateSettings.errorMessage);
        }

        return Promise.resolve({
            additionalProcessIds: [],
            tags: []
        });
    }

    public async startCollectDiagnostics(processId: number, additionalProcessIds: number[], diagSessionId: number): Promise<void> {
        if (this.config.recording.result === "success") {
            setTimeout(() => {
                this.platformEventHandler(PlatformEvent.collectProcessing, {
                    diagSessionId,
                    files: this.config.recording.files
                });
                setTimeout(() => {
                    this.platformEventHandler(PlatformEvent.collectDone, {
                        diagSessionId,
                        status: RecordingStatus.success
                    });
                }, this.config.recording.delayToDone);
            }, this.config.recording.delayToProcessing);
        }

        if (this.config.recording.result === "error") {
            setTimeout(() => {
                this.platformEventHandler(PlatformEvent.collectDone, {
                    diagSessionId,
                    status: RecordingStatus.error,
                    errorMessage: this.config.recording.errorMessage
                });
            }, this.config.recording.delayToProcessing);
        }

        if (this.config.recording.result === "cancel") {
            setTimeout(() => {
                this.platformEventHandler(PlatformEvent.collectDone, {
                    diagSessionId,
                    status: RecordingStatus.cancel
                });
            }, this.config.recording.delayToProcessing);
        }
    }

    public async stopCollectDiagnostics(): Promise<void> {
        // intentionally no-op
    }

    public async viewFile(filePath: string): Promise<void> {
        if (this.config.viewFile?.errorMessage) {
            throw new Error(this.config.viewFile.errorMessage);
        }
        alert(`Content of file: ${filePath}`);
    }

    public getScreenshot = async (thumbnail: boolean): Promise<ImageInfo> => {
        if (this.config.getScreenshot?.errorMessage) {
            throw new Error(this.config.getScreenshot.errorMessage);
        }

        return import("./screenshot")
            .then((screenshot) => ({
                dataUrl: _.cloneDeep(screenshot.default),
                width: 479,
                height: 253
            }));
    }

    public selectFiles = async (): Promise<FileSelection> => {
        if (this.config.selectFiles?.errorMessage) {
            throw new Error(this.config.selectFiles.errorMessage);
        }

        return this.additionalFiles.length === 0
            ? { includedFilePaths: [], deniedFilePaths: [] }
            : _.cloneDeep(this.additionalFiles.shift());
    }

    public reserveNewTrackingId = async (): Promise<TrackingIdInfo> => {
        if (this.config.reserveNewTrackingId?.errorMessage) {
            throw new Error(this.config.reserveNewTrackingId.errorMessage);
        }

        return {
            trackingId: Math.random().toString(36).substring(2, 15), // e.g. "akw36g4onqw"
        };
    }

    public async startUploadingFiles(config: FileUploadConfig, screenshot?: ScreenshotCropConfig): Promise<void> {
        if (this.config.upload?.errorMessage) {
            throw new Error(this.config.upload.errorMessage);
        }

        console.log(this.startGroup("startUploadingFiles"));
        console.log(config);
        console.log(screenshot);
        console.log(this.endGroup("startUploadingFiles"));
    }

    public chooseAccount = async (vsid: string): Promise<void> => {
        // intentionally no-op
    }
}
